import type { AutomationTrigger } from '@emailforge/schemas';
import { Button, Chip, Input, Select } from '@emailforge/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import {
  useCreateAutomationMutation,
  useDeleteAutomationMutation,
  useListAutomationsQuery,
  useToggleAutomationMutation,
} from '@/features/automations/automationsApi';
import { useListTemplatesQuery } from '@/features/templates/templatesApi';
import { t } from '@/lib/i18n';

const TRIGGER_CATALOG: Array<{
  trigger: AutomationTrigger;
  label: string;
  description: string;
  category: string;
}> = [
  {
    trigger: 'shopify_order_paid',
    label: 'Shopify · order paid',
    description: 'Send an order confirmation when a Shopify order is paid.',
    category: 'ecommerce',
  },
  {
    trigger: 'shopify_abandoned_cart',
    label: 'Shopify · abandoned cart',
    description: '3-email drip over 1h / 24h / 72h.',
    category: 'ecommerce',
  },
  {
    trigger: 'stripe_invoice_payment_failed',
    label: 'Stripe · invoice payment failed',
    description: 'Send a dunning email with portal link.',
    category: 'billing',
  },
  {
    trigger: 'webhook_generic',
    label: 'Generic webhook',
    description: 'Send on any HTTP POST with templateId + to + variables.',
    category: 'custom',
  },
  {
    trigger: 'onboarding_drip',
    label: 'Onboarding drip',
    description: 'Day 0 / 3 / 7 / 14 emails after signup.',
    category: 'lifecycle',
  },
  {
    trigger: 'sheet_row_added',
    label: 'Google Sheets · row added',
    description: 'Send when a row is marked in a spreadsheet.',
    category: 'data',
  },
];

export function AutomationsScreen() {
  const navigate = useNavigate();
  const { data: automations, isLoading } = useListAutomationsQuery();
  const { data: templates } = useListTemplatesQuery();
  const [create, { isLoading: isCreating }] = useCreateAutomationMutation();
  const [toggle] = useToggleAutomationMutation();
  const [remove] = useDeleteAutomationMutation();

  const [showCatalog, setShowCatalog] = useState(false);
  const [selectedTrigger, setSelectedTrigger] = useState<AutomationTrigger | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [name, setName] = useState<string>('');

  const submit = async () => {
    if (!selectedTrigger || !selectedTemplateId || !name) return;
    await create({ name, triggerType: selectedTrigger, templateId: selectedTemplateId }).unwrap();
    setShowCatalog(false);
    setSelectedTrigger(null);
    setSelectedTemplateId('');
    setName('');
  };

  return (
    <>
      <TopBar
        crumbs={[t('crumbs.configure'), t('nav.automations')]}
        right={
          !showCatalog ? (
            <Button variant="flame" size="sm" onClick={() => setShowCatalog(true)}>
              <Icon.plus size={13} /> New automation
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setShowCatalog(false)}>
              Cancel
            </Button>
          )
        }
      />
      <div style={{ flex: 1, padding: 24, overflowY: 'auto', background: 'var(--paper-2)' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto' }}>
          {showCatalog ? (
            <div className="mf-card" style={{ padding: 24, maxWidth: 680 }}>
              <h2 className="display" style={{ fontSize: 22, margin: '0 0 6px' }}>
                New automation
              </h2>
              <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 20 }}>
                Pick a trigger, bind it to a template, and we'll clone the n8n workflow for your tenant.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <div
                    className="mono"
                    style={{
                      fontSize: 10.5,
                      color: 'var(--ink-3)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.06,
                      marginBottom: 8,
                    }}
                  >
                    Trigger
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                    {TRIGGER_CATALOG.map((t) => (
                      <button
                        key={t.trigger}
                        className="mf-card"
                        onClick={() => {
                          setSelectedTrigger(t.trigger);
                          if (!name) setName(t.label);
                        }}
                        style={{
                          padding: 12,
                          textAlign: 'left',
                          cursor: 'pointer',
                          border:
                            selectedTrigger === t.trigger ? '1px solid var(--flame)' : '1px solid var(--line)',
                          background: selectedTrigger === t.trigger ? 'var(--flame-soft)' : 'var(--paper)',
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 550 }}>{t.label}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.4 }}>
                          {t.description}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div
                    className="mono"
                    style={{
                      fontSize: 10.5,
                      color: 'var(--ink-3)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.06,
                      marginBottom: 6,
                    }}
                  >
                    Name
                  </div>
                  <Input  value={name} onChange={(e) => setName(e.target.value)} />
                </div>

                <div>
                  <div
                    className="mono"
                    style={{
                      fontSize: 10.5,
                      color: 'var(--ink-3)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.06,
                      marginBottom: 6,
                    }}
                  >
                    Template
                  </div>
                  <Select
                    
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}>
                    <option value="">Pick a template…</option>
                    {(templates ?? []).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Select>
                </div>

                <Button variant="flame" onClick={submit}
                  disabled={!selectedTrigger || !selectedTemplateId || !name || isCreating}>
                  {isCreating ? 'Creating…' : 'Create automation'}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <h1 className="display" style={{ fontSize: 32, margin: '0 0 8px' }}>
                Automations
              </h1>
              <p style={{ color: 'var(--ink-2)', fontSize: 14, margin: '0 0 20px' }}>
                Each automation maps to a versioned n8n workflow under the hood. We handle tenant isolation and retries
                for you.
              </p>

              {isLoading && (
                <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  Loading…
                </div>
              )}

              {!isLoading && (automations ?? []).length === 0 && (
                <div className="mf-card" style={{ padding: 40, textAlign: 'center' }}>
                  <div
                    className="mono"
                    style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.08,
                      color: 'var(--ink-3)',
                    }}
                  >
                    No automations yet
                  </div>
                  <h2 className="display" style={{ fontSize: 22, margin: '10px 0 8px' }}>
                    Wire up your first trigger
                  </h2>
                  <p style={{ fontSize: 13.5, color: 'var(--ink-2)', maxWidth: 440, margin: '0 auto 20px' }}>
                    Shopify order-paid, Stripe invoice-failed, generic webhook — all with retries and tenant isolation.
                  </p>
                  <Button variant="flame" onClick={() => setShowCatalog(true)}>
                    <Icon.plus size={13} /> New automation
                  </Button>
                </div>
              )}

              {(automations ?? []).length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {automations?.map((a) => (
                    <div
                      key={a.id}
                      className="mf-card"
                      style={{
                        padding: 16,
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: 16,
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                          <div style={{ fontSize: 14, fontWeight: 550 }}>{a.name}</div>
                          <Chip variant={a.isActive ? 'live' : 'draft'}>
                            {a.isActive ? 'active' : 'paused'}
                          </Chip>
                        </div>
                        <div
                          className="mono"
                          style={{
                            fontSize: 10.5,
                            color: 'var(--ink-3)',
                            textTransform: 'uppercase',
                            letterSpacing: 0.06,
                          }}
                        >
                          {a.triggerType.replace(/_/g, ' ')} · {a.totalRuns} runs
                          {a.lastRunAt && ` · last ${new Date(a.lastRunAt).toLocaleString()}`}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/app/automations/${a.id}/advanced`)}
                        >
                          <Icon.bolt size={12} /> Edit flow
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => toggle({ id: a.id, active: !a.isActive })}
                        >
                          {a.isActive ? 'Pause' : 'Activate'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={async () => {
                            if (window.confirm('Delete this automation?')) {
                              await remove(a.id).unwrap();
                            }
                          }}
                        >
                          <Icon.trash size={12} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
